import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { createCheckoutSessionApi } from '@/lib/api/teamsApi';
import { getMyHistoryApi, getMyStatsApi } from '@/lib/api/usersApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr } from '@shared';
import { PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  ChevronRight,
  CreditCard,
  ExternalLink,
  Loader2,
  Search,
  Trophy,
} from 'lucide-react';
import { toast } from 'sonner';

const ADMIN_URL = (import.meta.env.VITE_URL_ADMIN as string | undefined) ?? 'http://localhost:5231';

const PAYMENT_STATUS_STYLES: Record<PaymentStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-700 border-amber-200',
  COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  FAILED: 'bg-red-100 text-red-600 border-red-200',
  REFUNDED: 'bg-slate-100 text-slate-600 border-slate-200',
  PARTIALLY_REFUNDED: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
};

const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  PENDING: 'En attente de paiement',
  COMPLETED: 'Payé',
  FAILED: 'Echec paiement',
  REFUNDED: 'Remboursé',
  PARTIALLY_REFUNDED: 'Remboursé partiel',
};

const REGISTRATION_STATUS_LABELS: Record<TeamRegistrationStatus, string> = {
  PENDING: 'En attente',
  CONFIRMED: 'Confirmée',
  REJECTED: 'Refusée',
  CANCELLED: 'Annulée',
};

export function MemberQuickActions() {
  return (
    <div className='mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
      <Link
        to={ROUTES.SEARCH_CONCOURS}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-blue-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-100'>
          <Search className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Trouver un concours</p>
          <p className='text-xs text-slate-500'>Rechercher par ville ou région</p>
        </div>
        <ArrowRight className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-blue-500 group-hover:translate-x-0.5' />
      </Link>

      <a
        href={`${ADMIN_URL}/player/dashboard`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-indigo-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition group-hover:bg-indigo-100'>
          <Calendar className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Mon tableau de bord</p>
          <p className='text-xs text-slate-500'>Statistiques et historique</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-indigo-500' />
      </a>

      <a
        href={`${ADMIN_URL}/player/inscriptions`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-emerald-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition group-hover:bg-emerald-100'>
          <Calendar className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Mes inscriptions</p>
          <p className='text-xs text-slate-500'>Concours à venir</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-emerald-500' />
      </a>
    </div>
  );
}

export function MemberStats() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.myStats(),
    queryFn: getMyStatsApi,
    enabled: isAuthenticated,
  });

  if (statsLoading || !isAuthenticated) {
    return (
      <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4'>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className='h-24 animate-pulse rounded-2xl bg-slate-100' />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    { label: 'Concours', value: stats.overview.totalCompetitions, color: 'text-blue-600' },
    { label: 'Victoires', value: stats.overview.wins, color: 'text-emerald-600' },
    { label: 'Podiums', value: stats.overview.podiums, color: 'text-amber-600' },
    { label: 'Taux victoire', value: `${Math.round(stats.overview.winRate)}%`, color: 'text-indigo-600' },
  ];

  return (
    <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4'>
      {statCards.map(card => (
        <div key={card.label} className='rounded-2xl bg-white border border-slate-100 shadow-sm p-5 text-center'>
          <p className={`text-2xl font-extrabold ${card.color}`}>{card.value}</p>
          <p className='mt-1 text-xs font-medium text-slate-500'>{card.label}</p>
        </div>
      ))}
    </div>
  );
}

export function MemberRecentRegistrations() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const [payingTeamId, setPayingTeamId] = useState<number | null>(null);

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: queryKeys.myHistory({ page: 1, limit: 5 }),
    queryFn: () => getMyHistoryApi({ page: 1, limit: 5 }),
    enabled: isAuthenticated,
  });

  const recentEntries = historyData?.data ?? [];

  async function handlePay(teamId: number, competitionId: number) {
    setPayingTeamId(teamId);
    try {
      const successUrl = `${window.location.origin}/concours/${String(competitionId)}?payment=success`;
      const cancelUrl = `${window.location.origin}/concours/${String(competitionId)}?payment=cancel`;
      const { checkoutUrl } = await createCheckoutSessionApi(teamId, successUrl, cancelUrl);
      window.location.assign(checkoutUrl);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de la création de la session de paiement');
      setPayingTeamId(null);
    }
  }

  return (
    <div className='mb-6 rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
      <div className='mb-5 flex items-center justify-between'>
        <h2 className='flex items-center gap-2 text-base font-bold text-slate-900'>
          <Calendar className='h-4 w-4 text-blue-500' />
          Inscriptions récentes
        </h2>
        <a
          href={`${ADMIN_URL}/player/inscriptions`}
          className='inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700'
        >
          Voir tout <ExternalLink className='h-3 w-3' />
        </a>
      </div>

      {historyLoading || !isAuthenticated ? (
        <div className='space-y-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className='h-16 animate-pulse rounded-xl bg-slate-100' />
          ))}
        </div>
      ) : recentEntries.length === 0 ? (
        <div className='flex flex-col items-center gap-3 py-8 text-center'>
          <Trophy className='h-10 w-10 text-slate-200' />
          <p className='text-sm text-slate-400 font-medium'>Aucune inscription pour l&apos;instant.</p>
          <Link
            to={ROUTES.SEARCH_CONCOURS}
            className='inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-colors'
          >
            Trouver un concours <ArrowRight className='h-4 w-4' />
          </Link>
        </div>
      ) : (
        <div className='divide-y divide-slate-50'>
          {recentEntries.map(entry => {
            const isPendingPayment = entry.paymentStatus === 'PENDING';
            const isCaptain = entry.role === 'CAPTAIN';
            const isActive = entry.registrationStatus !== 'CANCELLED' && entry.registrationStatus !== 'REJECTED';
            const canPay = isPendingPayment && isCaptain && isActive;
            const payStatusStyle =
              !isActive && isPendingPayment
                ? 'bg-slate-100 text-slate-400 border-slate-200'
                : (PAYMENT_STATUS_STYLES[entry.paymentStatus] ?? 'bg-slate-100 text-slate-500 border-slate-200');
            const payStatusLabel =
              !isActive && isPendingPayment
                ? 'Sans objet'
                : (PAYMENT_STATUS_LABELS[entry.paymentStatus] ?? String(entry.paymentStatus));
            const regStatusLabel =
              REGISTRATION_STATUS_LABELS[entry.registrationStatus] ?? String(entry.registrationStatus);
            const isThisOnePaying = payingTeamId === entry.teamId;

            return (
              <div
                key={entry.teamId}
                className='flex flex-col sm:flex-row sm:items-center sm:gap-4 hover:bg-slate-50 p-3 rounded-lg'
              >
                <div className='flex-1 min-w-0'>
                  <p className='truncate font-bold text-slate-900 text-sm'>
                    <Link to={`/concours/${String(entry.competition.id)}`}>{entry.competition.name}</Link>
                  </p>
                  <div className='mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-400'>
                    <span>{formatDateAbbreviatedFr(entry.competition.date)}</span>
                    {entry.competition.city && <span>{entry.competition.city}</span>}
                    <span className='text-slate-300'>|</span>
                    <span>
                      Équipe : <span className='font-medium text-slate-600'>{entry.teamName}</span>
                    </span>
                    {isCaptain && (
                      <span className='rounded-md bg-blue-50 px-1.5 py-0.5 font-semibold text-blue-600'>Capitaine</span>
                    )}
                  </div>
                </div>

                <div className='flex shrink-0 flex-wrap items-center gap-2'>
                  <span className='rounded-full border px-2.5 py-0.5 text-xs font-semibold text-slate-500 bg-slate-50 border-slate-200'>
                    {regStatusLabel}
                  </span>
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${payStatusStyle}`}>
                    {payStatusLabel}
                  </span>
                  {canPay && (
                    <button
                      onClick={() => void handlePay(entry.teamId, entry.competition.id)}
                      disabled={payingTeamId !== null}
                      className='inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600 disabled:opacity-60 cursor-pointer'
                    >
                      {isThisOnePaying ? (
                        <Loader2 className='h-3 w-3 animate-spin' />
                      ) : (
                        <CreditCard className='h-3 w-3' />
                      )}
                      Payer maintenant
                    </button>
                  )}
                  {isPendingPayment && !isCaptain && isActive && (
                    <span className='inline-flex items-center gap-1 text-xs text-amber-600 font-medium'>
                      <AlertCircle className='h-3 w-3' />
                      En attente du capitaine
                    </span>
                  )}

                  <Link
                    to={`/concours/${String(entry.competition.id)}`}
                    className='flex flex-row items-end gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700'
                  >
                    Voir le concours
                    <ChevronRight className='h-4 w-4' />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
